import React, { useContext, useMemo } from 'react';
import { FormikContextType } from 'formik';
import { useTranslation } from 'react-i18next';
import { UserContext, userRoles } from '../../../context/UserContext';
import FormikCheckboxGroupAccordion, {
  CheckboxOption,
} from '../../../components/formikComponents/FormikCheckboxGroupAccordion';
import { hasModule, isInFeature } from '../../../helpers/helpers';
import { STAFF_ROLES_CACHE_KEY } from '../../../constants';
import userLabsFeatures from '../../../helpers/userLabsFeatures';

const adminTillOptions = [
  { field: 'adminOptsTillAdmin' },
  { field: 'adminOptsTillSettings' },
  { field: 'adminOptsBackOffice', tooltip: 'adminOptsBackOfficeTooltip' },
  {
    field: 'adminOptsQuickAdd',
    depends: 'adminOptsBackOffice',
    tooltip: 'adminOptsQuickAddTooltip',
    features: [userLabsFeatures.quickAddProduct],
  },
  {
    field: 'adminOptsClockingSlip',
    tooltip: 'adminOptsClockingSlipTooltip',
    modules: [userRoles.modulePayroll],
  },
  { field: 'adminOptsManagerOverride', features: [userLabsFeatures.managerDeviceLicenseOverride] },
  { field: 'adminOptsPettyCash' },
  { field: 'adminOptsFloatAdjust' },
  { field: 'adminOptsNoSale' },
  { field: 'adminOptsTillClosing' },
  {
    field: 'adminOptsTillClosing_BlindEOD',
    depends: 'adminOptsTillClosing',
    tooltip: 'adminOptsTillClosing_BlindEODTooltip',
  },
  { field: 'adminOptsTipOutSelf' },
  { field: 'adminOptsTipOutOthers' },
  { field: 'adminOptsCanWaiterBank', modules: [userRoles.moduleWaiterBanking] },
  {
    field: 'adminOptsOrderAllocationServer',
    tooltip: 'adminOptsOrderAllocationServerTooltip',
    features: [userLabsFeatures.orderAllocation],
  },
  {
    field: 'adminOptsOrderAllocationTeamServer',
    tooltip: 'adminOptsOrderAllocationTeamServerTooltip',
    features: [userLabsFeatures.orderAllocation],
  },
  {
    field: 'adminOptsOrderAllocationTableManager',
    tooltip: 'adminOptsOrderAllocationTableManagerTooltip',
    features: [userLabsFeatures.orderAllocation],
  },
];

const AdminTillAccessForm = ({
  formik,
  objectId,
}: {
  formik: FormikContextType<unknown>;
  objectId: string;
}) => {
  const { t } = useTranslation();
  const { userFeatures, userModules } = useContext(UserContext);

  const options: CheckboxOption[] = useMemo(() => {
    const currOpts: CheckboxOption[] = [];
    adminTillOptions.forEach((option) => {
      if (option.modules && option.modules.length > 0) {
        const hasAllModules = option.modules.every((module) => hasModule(userModules, module));
        if (!hasAllModules) {
          return;
        }
      }

      if (option.features && option.features.length > 0) {
        const hasAllFeatures = option.features.every((labFeature) =>
          isInFeature(userFeatures, labFeature)
        );
        if (!hasAllFeatures) {
          return;
        }
      }

      if (option.depends && formik.values?.[option.depends] === false) {
        return;
      }

      currOpts.push({
        field: option.field,
        label: t(`screens.staffRoles.${option.field}`),
        tooltip: option.tooltip
          ? t(`screens.staffRoles.${option.tooltip}`)
          : undefined,
      });
    });

    return currOpts;
  }, [formik.values, userModules, t]);

  return (
    <FormikCheckboxGroupAccordion
      formik={formik}
      formName={STAFF_ROLES_CACHE_KEY}
      objectId={objectId}
      field="adminOptions"
      label={t('screens.staffRoles.adminOptions')}
      checkboxList={options}
    />
  );
};

export default AdminTillAccessForm;
